import React, { useState, useMemo } from 'react';
import get from 'lodash/get';
import { Button, CircularProgress } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import intl from 'react-intl-universal';

import { useStyles } from './ContactForm.Styles';
import { defaultButtonProps } from './DefaultProps';

import { create } from 'api/freemiumAccount';
import useInputWithError from 'hooks/useInputWithError';
import { validate } from 'validation/email';
import { validate as validateName } from 'validation/name';
import { match, validate as validatePassword } from 'validation/password';
import { getAnalyticsMetadata } from 'utils/analytics';
import { supportsWebapp } from 'utils/browser';
import { localizationUtils } from '@lifesize/clients.localization';
import { determineBrowserLocale } from '../../utils/locale';

const textFieldProps: TextFieldProps = {
  fullWidth: true,
  required: true,
  variant: 'outlined'
};

interface Props {
  onSignUpSuccess: (email: string, password: string) => void;
  buttonText: string;
}

function getFriendlyError(srcError: string) {
  if (srcError && srcError.includes('EMAIL_EXISTS')) {
    const launcherUrl = process.env.REACT_APP_LAUNCHER_URL;
    const webappUrl = process.env.REACT_APP_WEBAPP_URL;
    const webappSupported = supportsWebapp();
    const loginLink = webappSupported ? webappUrl : launcherUrl;
    return intl.getHTML('components.ContactForm.accountAlreadyExistsError', {
      loginLink
    });
  }
  return intl.get('components.ContactForm.accountCreationError');
}

const textLink = (href: string, text: string) =>
  `<a style="font-weight: 600; text-decoration: none" href="${href}">${text}</a>`;

export default function ContactForm(props: Props) {
  const classes = useStyles();
  const [signUpDisabled, setSignUpDisabled] = useState(false);
  const [requestError, setRequestError] = useState('');
  const firstNameInput = useInputWithError('');
  const lastNameInput = useInputWithError('');
  const companyNameInput = useInputWithError('');
  const emailInput = useInputWithError('');
  const phoneInput = useInputWithError('');
  const passwordInput = useInputWithError('');
  const passwordConfInput = useInputWithError('');

  async function onContinue() {
    setRequestError('');
    if (!validateName(firstNameInput.value, firstNameInput.setError)) return;
    if (!validateName(lastNameInput.value, lastNameInput.setError)) return;
    if (!validateName(companyNameInput.value, companyNameInput.setError)) return;
    if (!validate(emailInput.value, emailInput.setError)) return;
    if (!validateName(phoneInput.value, phoneInput.setError)) return;
    if (!validatePassword(passwordInput.value, passwordInput.setError)) return;
    if (!match(passwordInput.value, passwordConfInput.value, passwordConfInput.setError)) return;
    try {
      setSignUpDisabled(true);
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY, { action: 'creating_freemium_account' });
      });
      const analyticsMetadata = getAnalyticsMetadata();
      await create(firstNameInput.value, lastNameInput.value, emailInput.value, passwordInput.value, analyticsMetadata, companyNameInput.value, phoneInput.value);
      props.onSignUpSuccess(emailInput.value, passwordInput.value);
    } catch (error) {
      console.warn(error);
      const srcError = get(error, 'response.data.result');
      setRequestError(getFriendlyError(srcError));
      setSignUpDisabled(false);
    }
  }

  const locale = determineBrowserLocale();
  const languageId = useMemo(() => localizationUtils.getLanguageId(locale), [locale]);
  const privacyPolicyUrl = useMemo(() => localizationUtils.getPrivacyPolicy(languageId), [languageId]);
  const tosUrl = useMemo(() => localizationUtils.getTermsOfService(languageId), [languageId]);

  return (
    <Box>
      <h2 className={classes.heading}>{intl.get('components.ContactForm.header')}</h2>
      <Box mt={2}>
        <form>
          <TextField
            {...textFieldProps}
            className={classes.textField}
            error={firstNameInput.hasError}
            helperText={firstNameInput.error}
            id="input-first-name"
            name="fname"
            autoComplete="given-name"
            label={intl.get('components.ContactForm.firstName')}
            onChange={e => firstNameInput.setValue(e.target.value)}
            value={firstNameInput.value}
          />
          <TextField
            {...textFieldProps}
            className={classes.textField}
            error={lastNameInput.hasError}
            helperText={lastNameInput.error}
            id="input-last-name"
            name="lname"
            autoComplete="family-name"
            label={intl.get('components.ContactForm.lastName')}
            onChange={e => lastNameInput.setValue(e.target.value)}
            value={lastNameInput.value}
          />
          <TextField
            {...textFieldProps}
            className={classes.textField}
            error={companyNameInput.hasError}
            helperText={companyNameInput.error}
            id="input-company-name"
            name="companyname"
            autoComplete="company-name"
            label={intl.get('components.ContactForm.companyName')}
            onChange={e => companyNameInput.setValue(e.target.value)}
            value={companyNameInput.value}
          />
          <TextField
            {...textFieldProps}
            className={classes.textField}
            error={emailInput.hasError}
            helperText={emailInput.error}
            id="input-email"
            label={intl.get('components.ContactForm.email')}
            onChange={e => emailInput.setValue(e.target.value)}
            type="email"
            name="email"
            autoComplete="email"
            value={emailInput.value}
          />
          <TextField
            {...textFieldProps}
            className={classes.textField}
            error={phoneInput.hasError}
            helperText={phoneInput.error}
            id="input-phone"
            name="phone"
            autoComplete="phone"
            label={intl.get('components.ContactForm.phone')}
            onChange={e => phoneInput.setValue(e.target.value)}
            value={phoneInput.value}
          />
          <TextField
            {...textFieldProps}
            className={classes.textField}
            error={passwordInput.hasError}
            helperText={passwordInput.error}
            id="input-password"
            label={intl.get('components.ContactForm.password')}
            type="password"
            autoComplete="new-password"
            onChange={e => passwordInput.setValue(e.target.value)}
            value={passwordInput.value}
          />
          <TextField
            {...textFieldProps}
            className={classes.textField}
            error={passwordConfInput.hasError}
            helperText={passwordConfInput.error}
            id="input-confirm-password"
            label={intl.get('components.ContactForm.passwordConfirm')}
            type="password"
            autoComplete="new-password"
            onChange={e => passwordConfInput.setValue(e.target.value)}
            value={passwordConfInput.value}
          />
        </form>
        <Box my={2}>
          <Button
            {...defaultButtonProps}
            className={classes.continueButton}
            disabled={signUpDisabled}
            onClick={onContinue}
            id="continueButton"
          >
            {signUpDisabled ? (
              <CircularProgress
                variant="indeterminate"
                disableShrink
                size={24}
                thickness={4}
              />
            ) : props.buttonText}
          </Button>
        </Box>
        {requestError && <p className={classes.errorParagraph}>{requestError}</p>}
        <p
          className={classes.termsParagraph}
          dangerouslySetInnerHTML={{
            __html: intl
              .get('components.ContactForm.termsConditions')
              .replace('%s', textLink(privacyPolicyUrl || '', intl.get('components.ContactForm.privacyPolicyLink')))
              .replace('%s', textLink(tosUrl || '', intl.get('components.ContactForm.termsLink')))
          }}
        ></p>
      </Box>
    </Box>
  );
}
